import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import QrScanner from 'qr-scanner';

interface QRScannerProps {
    onResult: (result: string) => void;
    onError: (error: Error) => void;
    isActive: boolean;
}

interface QRScannerHandle {
    stopScanner: () => void;
}

const QRScanner = forwardRef<QRScannerHandle, QRScannerProps>(({ onResult, onError, isActive }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const scannerRef = useRef<QrScanner | null>(null);

    const cleanup = () => {
        console.log('QRScanner: Cleanup called, scanner exists:', !!scannerRef.current);
        if (scannerRef.current) {
            try {
                scannerRef.current.stop();
                scannerRef.current.destroy();
                scannerRef.current = null;
                console.log('QRScanner: Scanner stopped and destroyed');
            } catch (error) {
                console.error('QRScanner: Error during cleanup:', error);
            }
        }
        
        // Also try to stop any active video streams directly
        if (videoRef.current?.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach(track => {
                track.stop();
                console.log('QRScanner: Stopped track:', track.kind);
            });
            videoRef.current.srcObject = null;
        }
    };

    // Effect specifically for cleanup when isActive becomes false
    useEffect(() => {
        if (!isActive) {
            console.log('QRScanner: isActive became false, calling cleanup');
            cleanup();
        }
    }, [isActive]);

    // Effect for initializing scanner when active
    useEffect(() => {
        if (!isActive || !videoRef.current) {
            return;
        }

        console.log('QRScanner: Initializing scanner');
        const initScanner = async () => {
            try {
                const scanner = new QrScanner(
                    videoRef.current!,
                    (result) => {
                        onResult(result.data);
                    },
                    {
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                        preferredCamera: 'environment'
                    }
                );

                scannerRef.current = scanner;
                await scanner.start();
                console.log('QRScanner: Scanner started successfully');
            } catch (error) {
                onError(error instanceof Error ? error : new Error('Failed to start scanner'));
            }
        };

        initScanner();

        return cleanup;
    }, [isActive, onResult, onError]);

    // Expose cleanup method to parent component
    useImperativeHandle(ref, () => ({
        stopScanner: cleanup
    }));

    if (!isActive) {
        return null;
    }

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <video
                ref={videoRef}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                }}
            />
        </div>
    );
});

QRScanner.displayName = 'QRScanner';

export default QRScanner;